import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import type { DataTableColumn } from '../data-table-v2';
import type { SearchFieldColumn } from './types';
import { Checkbox } from '@/components/ui';

export const DEFAULT_DIALOG_PAGE_SIZE = 20;
export const DEFAULT_SUGGESTION_PAGE_SIZE = 15;
const SYNTHETIC_COLUMN_PREFIX = '__search_field__';

export const parseResponseData = <T,>(data: unknown): { items: T[]; total: number } => {
  if (Array.isArray(data)) {
    return { items: data as T[], total: data.length };
  }

  if (data && typeof data === 'object') {
    const response = data as Record<string, unknown>;

    if (Array.isArray(response.results)) {
      return {
        items: response.results as T[],
        total: Number(response.count ?? response.results.length)
      };
    }

    if (Array.isArray(response.data)) {
      return {
        items: response.data as T[],
        total: Number(response.total ?? response.count ?? response.data.length)
      };
    }

    if (Array.isArray(response.items)) {
      return {
        items: response.items as T[],
        total: Number(response.total ?? response.items.length)
      };
    }
  }

  return { items: [] as T[], total: 0 };
};

export const buildSearchUrl = (
  entitySlug: string | undefined,
  searchEndpoint?: string,
  otherSearchEndpoint?: string
) => {
  if (otherSearchEndpoint) {
    return otherSearchEndpoint;
  }

  if (!entitySlug || !searchEndpoint) {
    return null;
  }

  return `entities/${entitySlug}/erp/${normalizeEndpoint(searchEndpoint)}`;
};

export const getFieldValue = (row: any, field?: string): unknown => {
  if (!row || !field) return undefined;

  if (!field.includes('.')) {
    return row[field];
  }

  return field.split('.').reduce<unknown>((currentValue, currentField) => {
    if (currentValue == null || typeof currentValue !== 'object') {
      return undefined;
    }

    return (currentValue as Record<string, unknown>)[currentField];
  }, row);
};

const shouldUseResolvedValue = (resolvedValue: unknown, rawValue: unknown) => {
  if (resolvedValue === undefined) return false;
  if (typeof resolvedValue === 'number' && Number.isNaN(resolvedValue)) return false;
  if (resolvedValue === '' && rawValue !== '' && rawValue !== null && rawValue !== undefined) return false;
  return true;
};

const runResolver = (
  callback: ((...args: any[]) => unknown) | undefined,
  candidates: Array<() => unknown>,
  fallbackValue: unknown
) => {
  if (!callback) {
    return fallbackValue;
  }

  for (const candidate of candidates) {
    try {
      const resolvedValue = candidate();

      if (shouldUseResolvedValue(resolvedValue, fallbackValue)) {
        return resolvedValue;
      }
    } catch {
      continue;
    }
  }

  return fallbackValue;
};

export const resolveRowId = (row: any) => {
  const result = (() => {
    if (row?.uuid !== undefined && row?.uuid !== null && row?.uuid !== '') {
      return String(row.uuid);
    }

    if (row?.id !== undefined && row?.id !== null && row?.id !== '') {
      return String(row.id);
    }

    if (row?.ID !== undefined && row?.ID !== null && row?.ID !== '') {
      return String(row.ID);
    }

    if (row?.code !== undefined && row?.code !== null && row?.code !== '') {
      return String(row.code);
    }

    if (row?.ma !== undefined && row?.ma !== null && row?.ma !== '') {
      return String(row.ma);
    }

    return JSON.stringify(row);
  })();

  return result;
};

export const resolveSearchColumnValue = (row: any, column?: SearchFieldColumn | DataTableColumn) => {
  if (!column) {
    return {
      rawValue: undefined,
      value: undefined
    };
  }

  const rowId = resolveRowId(row);
  const rawValue = getFieldValue(row, column.field);
  const baseParams = {
    id: rowId,
    row,
    field: column.field,
    value: rawValue,
    formattedValue: rawValue,
    colDef: column,
    hasFocus: false,
    tabIndex: -1,
    api: {} as any,
    getValue: (fieldName: string) => getFieldValue(row, fieldName)
  };

  const valueAfterGetter = runResolver(
    column.valueGetter as ((...args: any[]) => unknown) | undefined,
    [
      () => (column.valueGetter as any)?.(rawValue, row, column, null),
      () => (column.valueGetter as any)?.(baseParams),
      () => (column.valueGetter as any)?.(row, column.field, rawValue, rowId)
    ],
    rawValue
  );

  const formattedValue = runResolver(
    column.valueFormatter as ((...args: any[]) => unknown) | undefined,
    [
      () => (column.valueFormatter as any)?.(valueAfterGetter),
      () => (column.valueFormatter as any)?.({ ...baseParams, formattedValue: valueAfterGetter }),
      () => (column.valueFormatter as any)?.(valueAfterGetter, row, column, null)
    ],
    valueAfterGetter
  );

  return {
    rawValue,
    value: formattedValue
  };
};

export const stringifyValue = (value: unknown): string => {
  if (value == null) return '';
  if (Array.isArray(value))
    return value
      .map(item => stringifyValue(item))
      .filter(Boolean)
      .join(', ');
  if (typeof value === 'object') return '';
  return String(value);
};

export const getDisplayText = <T,>(row: T | null | undefined, field?: keyof T | string) => {
  if (!row || !field) return '';
  return stringifyValue(getFieldValue(row, String(field)));
};

export const parseSearchCodes = (input: string, separator = ',') => {
  const uniqueCodes = new Set<string>();

  input
    .split(separator)
    .map(code => code.trim())
    .filter(Boolean)
    .forEach(code => uniqueCodes.add(code));

  return Array.from(uniqueCodes);
};

export const filterLocalRows = <T,>(rows: T[] = [], query: string) => {
  if (!query.trim()) {
    return rows;
  }

  const normalizedQuery = query.trim().toLowerCase();

  return rows.filter(row =>
    Object.values(row ?? {}).some(value => {
      if (value == null) return false;

      if (typeof value === 'object') {
        try {
          return JSON.stringify(value).toLowerCase().includes(normalizedQuery);
        } catch {
          return false;
        }
      }

      return String(value).toLowerCase().includes(normalizedQuery);
    })
  );
};

export const matchesExactCode = <T,>(row: T, columnDisplay: keyof T | undefined, code: string) => {
  const resolvedCode = getDisplayText(row, columnDisplay as string);
  return resolvedCode.trim().toLowerCase() === code.trim().toLowerCase();
};

const isLegacyCheckboxColumn = <T,>(column: SearchFieldColumn<T>) => {
  const field = column.field?.trim();

  return !field && column.type === 'boolean' && Boolean(column.renderCell || column.renderHeader);
};

const isSelectionColumn = <T,>(column: SearchFieldColumn<T>) =>
  column.field?.trim() === 'checkbox' || isLegacyCheckboxColumn(column);

export const normalizeSearchColumns = <T,>(searchColumns: SearchFieldColumn<T>[] = []): DataTableColumn[] => {
  return searchColumns
    .filter(column => !isSelectionColumn(column))
    .map((column, index) => {
      const resolvedField = column.field?.trim() ? column.field : `${SYNTHETIC_COLUMN_PREFIX}${index}`;
      const needsFieldResolver = resolvedField.includes('.') && !column.valueGetter;
      const shouldRenderCheckbox = column.checkboxSelection && !column.renderCell;
      const resolvedHeaderName =
        column.headerName ??
        (column.checkboxSelection || resolvedField.startsWith(SYNTHETIC_COLUMN_PREFIX) ? '' : column.field || '');

      return {
        ...column,
        field: resolvedField,
        headerName: resolvedHeaderName,
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
        type: column.type || 'string',
        valueGetter: needsFieldResolver
          ? (_: unknown, row: T) => getFieldValue(row, resolvedField)
          : (column.valueGetter as DataTableColumn['valueGetter']),
        valueFormatter: column.valueFormatter as DataTableColumn['valueFormatter'],
        renderCell:
          (column.renderCell as DataTableColumn['renderCell']) ||
          (shouldRenderCheckbox
            ? params => (
                <div className='flex h-full w-full items-center justify-center'>
                  <Checkbox checked={params.value === true || params.value === 1} disabled />
                </div>
              )
            : undefined)
      };
    });
};

export const hasSelectionCheckboxColumn = <T,>(searchColumns: SearchFieldColumn<T>[] = []) =>
  searchColumns.some(isSelectionColumn);

export const getSuggestionColumns = <T,>(
  searchColumns: SearchFieldColumn<T>[] = [],
  primaryField?: string,
  secondaryField?: string
) => {
  const visibleColumns = searchColumns.filter(column => {
    const field = column.field?.trim();

    return (
      Boolean(field) &&
      !field?.startsWith(SYNTHETIC_COLUMN_PREFIX) &&
      !isSelectionColumn(column) &&
      !column.checkboxSelection
    );
  });

  if (visibleColumns.length > 0) {
    return visibleColumns.slice(0, 2);
  }

  const fallbackColumns: SearchFieldColumn<T>[] = [];

  if (primaryField) {
    fallbackColumns.push({ field: primaryField });
  }

  if (secondaryField && secondaryField !== primaryField) {
    fallbackColumns.push({ field: secondaryField });
  }

  return fallbackColumns;
};
